import { useCallback, useMemo } from 'react';

import { Box, DatePicker } from '@frontierag/frontier-ui-library';
import { API_DATE_FORMAT, DEFAULT_DATE_FORMAT } from 'appConstants';
import dayjs from 'dayjs';
import { useField } from 'formik';

import styles from './styles';
import type { IDatePickerField } from './types';

const DatePickerField = ({
  name,
  saveFormat = API_DATE_FORMAT,
  clearable,
  required,
  ...rest
}: IDatePickerField) => {
  const [{ value }, { error, touched }, { setTouched, setValue }] = useField(name);

  const date = useMemo(() => (value ? dayjs(value, API_DATE_FORMAT) : null), [value]);

  const onChange = useCallback(
    async (date) => {
      await setValue(date && date.format(saveFormat), true);
    },
    [saveFormat, setValue],
  );

  const onClear = useCallback(async () => {
    await setValue('');
  }, [setValue]);

  const onClose = useCallback(async () => {
    if (!touched) {
      await setTouched(true, false);
    }
  }, [touched, setTouched]);

  const hasError = Boolean(error && touched);
  const helperText = hasError && error;

  return (
    <Box sx={styles}>
      <DatePicker
        name={name}
        value={date}
        onClose={onClose}
        onChange={onChange}
        format={DEFAULT_DATE_FORMAT}
        orientation="portrait"
        onClear={onClear}
        clearable={clearable}
        required={required}
        slotProps={{
          textField: {
            helperText: helperText,
            error: hasError,
            onBlur: onClose,
            placeholder: DEFAULT_DATE_FORMAT.toLowerCase(),
          },
        }}
        {...rest}
      />
    </Box>
  );
};

export default DatePickerField;
